import dayjs from 'dayjs';
import { i18n } from '../i18n';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { formatFileSizeLabel } from './fileSize';

dayjs.extend(customParseFormat);

/** Display format for dates across the app (e.g. 19/May/2026). */
export const DISPLAY_DATE_FORMAT = 'DD/MMM/YYYY';

/** Display format for date-time values (e.g. 19/May/2026, 10:30 AM). */
export const DISPLAY_DATETIME_FORMAT = 'DD/MMM/YYYY, hh:mm A';

/** Audit / system info fields (e.g. 08 Jun 2026, 4:31 PM). */
export const AUDIT_DATETIME_FORMAT = 'DD MMM YYYY, h:mm A';

/** ISO storage/API format. */
export const ISO_DATE_FORMAT = 'YYYY-MM-DD';

export const DATE_PLACEHOLDER = 'DD/MMM/YYYY';

export const DATE_RANGE_PLACEHOLDER = 'DD/MMM/YYYY - DD/MMM/YYYY';

const DATE_RANGE_SEP = ' - ';

function normalizeIsoDateTimeForDayjs(input: string): string {
  const normalizedInput = input
    .replace(/^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}(?::?\d{2})?)?)$/, '$1T$2')
    .replace(/([+-]\d{2})(?!:?\d{2})$/, '$1:00')
    .replace(/([+-]\d{2})(\d{2})$/, '$1:$2');

  // Some APIs return fractional seconds with micro/nano precision (e.g. 2026-05-22T10:19:59.945660Z).
  // JS Date/dayjs parsing is only reliable up to milliseconds, so trim to 3 fractional digits when present.
  return normalizedInput.replace(/(\.\d{3})\d+(Z|[+-]\d{2}:\d{2})$/, '$1$2');
}

/** Parse user or API input to ISO `YYYY-MM-DD`, or empty string if invalid. */
export function parseToIsoDate(input: string): string {
  const trimmed = input.trim();
  if (!trimmed) return '';

  if (dayjs(trimmed, ISO_DATE_FORMAT, true).isValid()) {
    return dayjs(trimmed, ISO_DATE_FORMAT, true).format(ISO_DATE_FORMAT);
  }

  const parsed = dayjs(trimmed, DISPLAY_DATE_FORMAT, true);
  if (parsed.isValid()) return parsed.format(ISO_DATE_FORMAT);

  const loose = dayjs(trimmed);
  if (loose.isValid()) return loose.format(ISO_DATE_FORMAT);

  return '';
}

/** Format an ISO or parseable date string for display. */
export function formatDisplayDate(value: string | null | undefined): string {
  if (!value?.trim()) return '';
  const iso = parseToIsoDate(value);
  if (!iso) return value.trim();
  return dayjs(iso).format(DISPLAY_DATE_FORMAT);
}

/** Format a date range for display (e.g. 01/Jan/2024 - 15/Jan/2024). */
export function formatDateRange(from: string, to?: string): string {
  return formatDateRangeInput(from, to);
}

/** Format a date range for the range input control. */
export function formatDateRangeInput(from: string, to?: string): string {
  const start = formatDisplayDate(from);
  if (!start) return '';
  if (!to?.trim()) return start;
  const end = formatDisplayDate(to);
  return end ? `${start}${DATE_RANGE_SEP}${end}` : start;
}

/** Parse a range input string into ISO from/to dates. */
export function parseDateRangeInput(text: string): { from: string; to: string } {
  const trimmed = text.trim();
  if (!trimmed) return { from: '', to: '' };

  const sepIndex = trimmed.indexOf(DATE_RANGE_SEP);
  if (sepIndex >= 0) {
    let from = parseToIsoDate(trimmed.slice(0, sepIndex).trim());
    let to = parseToIsoDate(trimmed.slice(sepIndex + DATE_RANGE_SEP.length).trim());
    if (from && to && from > to) [from, to] = [to, from];
    return { from, to };
  }

  const single = parseToIsoDate(trimmed);
  return { from: single, to: '' };
}

/** Format a date-time for display. */
export function formatDisplayDateTime(value: string | Date | null | undefined): string {
  if (value == null || value === '') return '';
  const dt =
    value instanceof Date
      ? dayjs(value)
      : dayjs(typeof value === 'string' ? normalizeIsoDateTimeForDayjs(value.trim()) : value);
  if (!dt.isValid()) {
    const iso = parseToIsoDate(String(value));
    if (iso) return dayjs(iso).format(DISPLAY_DATE_FORMAT);
    return String(value).trim();
  }
  return dt.format(DISPLAY_DATETIME_FORMAT);
}

/** Format audit read-only date-time per data dictionary (DD MMM YYYY, HH:MM AM/PM). */
export function formatAuditDateTime(value: string | Date | null | undefined): string {
  if (value == null || value === '') return '';
  const dt =
    value instanceof Date
      ? dayjs(value)
      : dayjs(typeof value === 'string' ? normalizeIsoDateTimeForDayjs(value.trim()) : value);
  if (!dt.isValid()) {
    const iso = parseToIsoDate(String(value));
    if (iso) return dayjs(iso).format(AUDIT_DATETIME_FORMAT);
    return String(value).trim();
  }
  return dt.format(AUDIT_DATETIME_FORMAT);
}

export function isValidIsoOrDisplayDate(input: string): boolean {
  return Boolean(parseToIsoDate(input));
}

/** Time portion for uploaded file display (e.g. 10:30 AM). */
export function formatUploadTimeLabel(iso: string | null | undefined): string {
  if (!iso?.trim()) return '';
  const dt = dayjs(normalizeIsoDateTimeForDayjs(iso.trim()));
  return dt.isValid() ? dt.format('hh:mm A') : '';
}

/** Documents table — CMS data dictionary (DD MMM YYYY, HH:MM AM/PM). */
export const DOCUMENT_TABLE_DATETIME_FORMAT = 'DD MMM YYYY, hh:mm A';

/** Format date-time for document table columns; supports ISO or API pre-formatted strings. */
export function formatDocumentTableDateTime(value: string | null | undefined): string {
  if (!value?.trim()) return '';
  const t = value.trim();

  const asTable = dayjs(t, DOCUMENT_TABLE_DATETIME_FORMAT, true);
  if (asTable.isValid()) return t;

  const asComm = dayjs(t, DISPLAY_COMMUNICATION_TIME_FORMAT, true);
  if (asComm.isValid()) return asComm.format(DOCUMENT_TABLE_DATETIME_FORMAT);

  const dt = dayjs(t);
  if (dt.isValid()) return dt.format(DOCUMENT_TABLE_DATETIME_FORMAT);

  const iso = parseToIsoDate(t);
  if (iso) {
    const fromIso = dayjs(iso);
    if (fromIso.isValid()) return fromIso.format(DOCUMENT_TABLE_DATETIME_FORMAT);
  }

  return t;
}

/** API / UI format for communication timestamps (e.g. 09 Apr 2025 11:20 AM). */
export const DISPLAY_COMMUNICATION_TIME_FORMAT = 'DD MMM YYYY hh:mm A';

const LEGACY_DISPLAY_COMMUNICATION_TIME_FORMAT = 'DD MMM YYYY • hh:mm A';

/** Communication message timestamp — supports ISO or API pre-formatted strings. */
export function formatCommunicationTime(value: string | null | undefined): string {
  if (!value?.trim()) return '';
  const t = value.trim();

  const asApiDisplay = dayjs(t, DISPLAY_COMMUNICATION_TIME_FORMAT, true);
  if (asApiDisplay.isValid()) return t;

  const asLegacyDisplay = dayjs(t, LEGACY_DISPLAY_COMMUNICATION_TIME_FORMAT, true);
  if (asLegacyDisplay.isValid()) return asLegacyDisplay.format(DISPLAY_COMMUNICATION_TIME_FORMAT);

  const dt = dayjs(t);
  if (dt.isValid()) return dt.format(DISPLAY_COMMUNICATION_TIME_FORMAT);

  const iso = parseToIsoDate(t);
  if (iso) {
    const fromIso = dayjs(iso);
    if (fromIso.isValid()) return fromIso.format(DISPLAY_COMMUNICATION_TIME_FORMAT);
  }

  return t;
}

/** Compact relative time for notification cards (e.g. 10 mins ago, Yesterday). */
export function formatRelativeTimeAgo(value: string | null | undefined): string {
  if (!value?.trim()) return '';
  const raw = value.trim();

  const asComm = dayjs(raw, DISPLAY_COMMUNICATION_TIME_FORMAT, true);
  const dt = asComm.isValid() ? asComm : dayjs(raw);
  if (!dt.isValid()) return raw;

  const diffMs = Date.now() - dt.valueOf();
  const secs = Math.floor(diffMs / 1000);
  if (secs < 60) return i18n.t('audit.justNow');
  const mins = Math.floor(secs / 60);
  if (mins < 60) return i18n.t('audit.minutesAgo', { count: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 24) return i18n.t('audit.hoursAgo', { count: hours });
  const days = Math.floor(hours / 24);
  if (days === 1) return i18n.t('audit.yesterday');
  if (days < 7) return i18n.t('audit.daysAgo', { count: days });
  return dt.format('DD MMM YYYY, hh:mm A');
}

/** Review table file meta (e.g. Uploaded 08 Apr 2025, 09:12 AM). */
export function formatReviewUploadMeta(iso: string | null | undefined): string {
  if (!iso?.trim()) return '';
  const dt = dayjs(iso);
  return dt.isValid() ? `Uploaded ${dt.format('DD MMM YYYY, hh:mm A')}` : '';
}

/** Review table meta line: file size and upload timestamp (e.g. 412 KB • Uploaded 08 Apr 2025, 09:12 AM). */
export function formatReviewFileMeta(
  fileSize: number | undefined,
  uploadTimeIso: string | null | undefined,
): string {
  const size = formatFileSizeLabel(fileSize);
  const uploaded = formatReviewUploadMeta(uploadTimeIso);
  if (size && uploaded) return `${size} • ${uploaded}`;
  return size || uploaded;
}
